import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import type {
	FilterCondition,
	FilterParams,
	MetadataTypes,
} from "epsm-api-types";
import {
	SCREENER_COLUMNS,
	type ScreenerColumn,
	type ScreenerFilterData,
} from "epsm-api-types/models";
import type React from "react";
import {
	createContext,
	type ReactNode,
	type RefObject,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import type { MultiValue, SingleValue } from "react-select";
import { formatValue } from "@/components/widgets/helpers/FormattingHelpers";
import {
	type ScreenerFilterDataOptions,
	UseData,
} from "@/components/widgets/Screener/services/UseData";
import type { Option } from "@/interfaces/Option";
import type { Result } from "@/interfaces/Result";
import type { ValueRange } from "@/interfaces/ValueRange";
import { getUnixTime } from "@/lib/utils";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { useWidgetView } from "./WidgetViewContext";

export interface FilterDataContextProps {
	value: ValueRange;
	setValue: React.Dispatch<React.SetStateAction<ValueRange>>;
	results: Result[];
	setResults: (value: Result[]) => void;
	resultsRef: React.RefObject<Result[]>;
	listOptions: MultiValue<Option>;
	setListOptions: (value: MultiValue<Option>) => void;
	executeFilterData: (
		options: ScreenerFilterDataOptions,
	) => Promise<ScreenerFilterData | null>;
	isLoading: boolean;
	filterData: ScreenerFilterData | null;
	updateResults: (
		newResult: Array<{
			name: string;
			condition: FilterCondition;
			value: unknown;
		}>,
		refetch: UseInfiniteQueryResult["refetch"],
	) => void;
	getOptions: (item: unknown, selectedColumn: ScreenerColumn) => Option;
	field: string;
	setField: (value: string) => void;
	isDate: () => boolean;
	getProcessedValue: (value: string | number) => number;
	selectedSubField: SingleValue<Option | null>;
	setSelectedSubField: React.Dispatch<
		React.SetStateAction<SingleValue<Option | null>>
	>;
	isResultSelectedRef: React.RefObject<boolean>;
	getSubFieldName: () => string | undefined;
	setNeedsRecalculation: (value: boolean) => void;
	isTotalCountLoading: boolean;
	hasRangePickerValueChangedRef: RefObject<boolean>;
}

const FilterDataContext = createContext<FilterDataContextProps | undefined>(
	undefined,
);

export const FilterDataProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [value, setValue] = useState<ValueRange>({
		min: "",
		max: "",
	});
	const [results, setResults] = useState<Result[]>([]);
	const resultsRef = useRef<Result[]>(results);
	const [listOptions, setListOptions] = useState<MultiValue<Option>>([]);
	const [field, setField] = useState("");
	const [selectedSubField, setSelectedSubField] =
		useState<SingleValue<Option | null>>(null);
	const isResultSelectedRef = useRef(false);
	const [needsRecalculation, setNeedsRecalculation] = useState(false);
	const [isTotalCountLoading, setIsTotalCountLoading] = useState(false);
	const hasRangePickerValueChangedRef = useRef(false);

	const { selectedColumn } = useWidgetView();

	const { execute } = UseData.create().useGetData();
	const {
		execute: executeFilterData,
		isLoading,
		data: filterData,
	} = UseData.create().useGetFilterData();

	const updateResults = (
		newResult: Array<{
			name: string;
			condition: FilterCondition;
			value: unknown;
		}>,
		refetch: UseInfiniteQueryResult["refetch"],
	) => {
		const nameMatchIndex = results.findIndex(
			(result) =>
				"name" in result.result[0] &&
				result.result[0].name === newResult[0].name,
		);
		const exactMatchRes = results.find(
			(result) => JSON.stringify(result) === JSON.stringify(newResult),
		);

		if (exactMatchRes) return;

		if (nameMatchIndex >= 0) {
			const updatedResults = [...results];
			updatedResults[nameMatchIndex].result = newResult;
			updatedResults[nameMatchIndex].totalCount =
				results[nameMatchIndex].totalCount;
			setResults(updatedResults);
			resultsRef.current = updatedResults;
		} else {
			const newResults = [...results, { result: newResult, totalCount: 0 }];
			setResults(newResults);
			resultsRef.current = newResults;
		}

		setNeedsRecalculation(true);
		refetch();
	};

	useEffect(() => {
		if (!needsRecalculation) return;

		const recalculateTotals = async () => {
			setIsTotalCountLoading(true);

			let cumulativeFilters: FilterParams = [];
			const cumulativeTypes: MetadataTypes = {};

			const promises = results.map(async (result) => {
				for (const filter of result.result) {
					if ("name" in filter) {
						const filterName = filter.name;
						const [mainField] = filterName.split(".");

						const column = SCREENER_COLUMNS.find(
							(item) => item.name === mainField,
						);

						if (column) {
							cumulativeTypes[filterName] = column.type;
						}
					}
				}

				cumulativeFilters = [...cumulativeFilters, ...result.result];

				const response = await execute({
					params: {
						filter: cumulativeFilters,
						total: "only",
						types: cumulativeTypes,
					},
				});

				return {
					...result,
					totalCount: response?.metadata?.total || 0,
				};
			});

			try {
				const recalculatedResults = await Promise.all(promises);

				setResults(recalculatedResults);
				resultsRef.current = recalculatedResults;
			} catch (error) {
				ErrorHandler.logError(error);
			} finally {
				setNeedsRecalculation(false);
				setIsTotalCountLoading(false);
			}
		};

		recalculateTotals();
	}, [needsRecalculation, execute, results]);

	const getOptions = useCallback(
		(value: unknown, selectedColumn: ScreenerColumn) => {
			return {
				value,
				label: formatValue(value, selectedColumn.type, selectedColumn.options),
			};
		},
		[],
	);

	const isDate = useCallback(() => {
		if (!selectedColumn.Screener) return false;

		return selectedColumn.Screener.type === "date";
	}, [selectedColumn]);

	const getProcessedValue = (value: string | number) => {
		if (isDate() && typeof value === "string") {
			return getUnixTime(value);
		}

		return value as number;
	};

	const getSubFieldName = () => {
		if (!selectedColumn.Screener) return;

		const name = selectedColumn.Screener.name;
		const subFieldName =
			selectedColumn.Screener.subFields &&
			(!selectedSubField
				? `${name}.${selectedColumn.Screener.subFields?.[0].name}`
				: `${name}.${selectedSubField.value}`);

		return subFieldName;
	};

	return (
		<FilterDataContext.Provider
			value={{
				value,
				setValue,
				results,
				setResults,
				resultsRef,
				listOptions,
				setListOptions,
				updateResults,
				executeFilterData,
				isLoading,
				filterData,
				getOptions,
				field,
				setField,
				isDate,
				getProcessedValue,
				selectedSubField,
				setSelectedSubField,
				isResultSelectedRef,
				getSubFieldName,
				setNeedsRecalculation,
				isTotalCountLoading,
				hasRangePickerValueChangedRef,
			}}
		>
			{children}
		</FilterDataContext.Provider>
	);
};

export const useFilterData = () => {
	const context = useContext(FilterDataContext);
	if (!context) {
		throw new Error("useFilterData must be used within a FilterDataProvider");
	}
	return context;
};
